/**
 * Documentation-app HTTP client (internal id: `vault`).
 *
 * All endpoints specific to the Documentation app — customers, documents,
 * attachments, vault membership, orphanage, snapshot history. Reuses
 * Core's `request` + token management (see
 * `core/lib/api.ts`) so 401 handling + Authorization injection stay in one
 * place.
 *
 * Note: three vault-related endpoints (`setKeypair`, `bootstrapVault`,
 * `myEnrolment`) live in Core's api because the Core session store calls
 * them during Master Code setup. The single-app collapse made the layer
 * boundary cosmetic — they could move here, but moving them is pure churn
 * since Core is the only consumer of the vault store anyway.
 */
import type {
  BackupRunNowResponse,
  BackupStatusResponse,
  EncryptedPayload,
  SuggestIconRequest,
  SuggestIconResponse,
  WrappedVaultKey,
} from '@dragonhq/protocol';
import { BASE, getAccessToken, request } from '../../../core/lib/api';

export const api = {
  // --- Vault membership ----------------------------------------------------
  listInvitees() {
    return request<{
      invitees: Array<{ id: string; username: string; email: string; publicKey: string }>;
    }>('/auth/vault/invitees');
  },
  listMembers() {
    return request<{
      members: Array<{
        userId: string;
        username: string;
        email: string;
        role: string;
        roleInVault: string;
        addedAt: string;
      }>;
    }>('/auth/vault/members');
  },
  inviteMember(userId: string, wrappedKey: WrappedVaultKey) {
    return request<{ ok: true }>('/auth/vault/members', {
      method: 'POST',
      body: JSON.stringify({ userId, wrappedKey }),
    });
  },
  removeMember(userId: string) {
    return request<{ ok: true }>(
      `/auth/vault/members/${encodeURIComponent(userId)}`,
      { method: 'DELETE' },
    );
  },

  // --- Scheduled Dropbox backup -------------------------------------------
  enrolBackupIdentity(body: {
    publicKey: string;
    encryptedPrivateKey: {
      cipher: { algorithm: 'aes-256-gcm'; iv: string; ciphertext: string };
      version: 1;
    };
    wrappedVaultKey: WrappedVaultKey;
  }) {
    return request<{ ok: true }>('/api/vault/backup/enrol', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  revokeBackupIdentity() {
    return request<{ ok: true }>('/api/vault/backup/revoke', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  getBackupStatus() {
    return request<BackupStatusResponse>('/api/vault/backup/status');
  },
  runBackupNow() {
    return request<BackupRunNowResponse>('/api/vault/backup/run-now', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  disconnectDropbox() {
    return request<{ ok: true }>('/api/vault/backup/disconnect-dropbox', {
      method: 'POST',
      body: JSON.stringify({}),
    });
  },
  /**
   * Initiate the Dropbox OAuth flow. Server validates admin auth, mints
   * the state cookie, and returns the Dropbox authorize URL. The caller
   * navigates the browser to `authorizeUrl` — the cookie set in this POST
   * persists for the round-trip back to `/auth/dropbox/callback`.
   */
  dropboxStart() {
    return request<{ ok: true; authorizeUrl: string }>(
      '/api/vault/backup/dropbox/start',
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    );
  },

  // --- Customers -----------------------------------------------------------
  listCustomers(opts: { since?: string; includeDeleted?: boolean; limit?: number; cursor?: string } = {}) {
    const params = new URLSearchParams();
    if (opts.since) params.set('since', opts.since);
    if (opts.includeDeleted) params.set('includeDeleted', '1');
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request<{ customers: CustomerDto[]; nextCursor?: string | null }>(
      `/api/customers${qs}`,
    );
  },
  createCustomer(id: string, payload: EncryptedPayload) {
    return request<{ id: string }>(`/api/customers`, {
      method: 'POST',
      body: JSON.stringify({ id, payload }),
    });
  },
  updateCustomer(id: string, payload: EncryptedPayload) {
    return request(`/api/customers/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify({ id, payload }),
    });
  },
  deleteCustomer(id: string) {
    return request(`/api/customers/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  restoreCustomer(
    id: string,
    payload?: EncryptedPayload,
    opts?: { cascadeChildren?: boolean },
  ) {
    const body: Record<string, unknown> = {};
    if (payload) body.payload = payload;
    if (opts?.cascadeChildren) body.cascadeChildren = true;
    return request<{ ok: true; cascadedDocs: number; cascadedAttachments: number }>(
      `/api/customers/${encodeURIComponent(id)}/restore`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      },
    );
  },

  // --- Documents + versions ------------------------------------------------
  listDocuments(opts: { customerId?: string; includeDeleted?: boolean; limit?: number; cursor?: string } = {}) {
    const params = new URLSearchParams();
    if (opts.customerId) params.set('customerId', opts.customerId);
    if (opts.includeDeleted) params.set('includeDeleted', '1');
    if (opts.limit) params.set('limit', String(opts.limit));
    if (opts.cursor) params.set('cursor', opts.cursor);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request<{ documents: DocumentDto[]; nextCursor?: string | null }>(
      `/api/documents${qs}`,
    );
  },
  createDocument(body: {
    id: string;
    customerId: string;
    parentId?: string | null;
    payload: EncryptedPayload;
    recoveredFromVersionId?: string;
  }) {
    return request<{ id: string }>(`/api/documents`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  updateDocument(
    id: string,
    body: {
      customerId: string;
      parentId?: string | null;
      payload: EncryptedPayload;
      /**
       * Optimistic-lock guard. When present, the server only applies the
       * write if the current row's version matches. Mismatch → 409 with
       * `{ currentVersion }` so the caller can refetch and merge. Omit for
       * non-Yjs callers (sidebar rename, customer restore) that rely on
       * last-writer-wins.
       */
      expectedVersion?: number;
    },
    opts: {
      /** Pass-through to fetch(). Lets pagehide flush survive tab close. */
      keepalive?: boolean;
      /**
       * Tells the server "this small ciphertext is intentional" so the
       * suspicious-shrink guard (≥75% reduction on a doc with version > 3)
       * accepts it instead of forcing a 409 CRDT-merge. Audit-logged.
       */
      allowShrink?: boolean;
    } = {},
  ) {
    const headers: Record<string, string> = {};
    if (opts.allowShrink) headers['X-Allow-Shrink'] = '1';
    return request<{ id: string; version: number }>(
      `/api/documents/${encodeURIComponent(id)}`,
      {
        method: 'PUT',
        body: JSON.stringify({ ...body, id }),
        keepalive: opts.keepalive,
        headers,
      },
    );
  },
  deleteDocument(id: string) {
    return request(`/api/documents/${encodeURIComponent(id)}`, { method: 'DELETE' });
  },
  restoreDocument(id: string, payload?: EncryptedPayload) {
    return request(`/api/documents/${encodeURIComponent(id)}/restore`, {
      method: 'POST',
      body: JSON.stringify(payload ? { payload } : {}),
    });
  },
  acquireLock(id: string) {
    return request<{ ok: boolean; expiresAt: string }>(
      `/api/documents/${encodeURIComponent(id)}/lock`,
      { method: 'POST' },
    );
  },
  releaseLock(id: string) {
    return request(`/api/documents/${encodeURIComponent(id)}/lock`, {
      method: 'DELETE',
    });
  },
  getVersions(id: string, opts: { before?: string; limit?: number } = {}) {
    const params = new URLSearchParams();
    if (opts.before) params.set('before', opts.before);
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request<{
      versions: Array<{
        id: string;
        documentId: string;
        authorId: string;
        payload: EncryptedPayload;
        createdAt: string;
      }>;
      nextBefore: string | null;
    }>(`/api/documents/${encodeURIComponent(id)}/versions${qs}`);
  },

  // --- Orphanage (soft-deleted recovery) -----------------------------------
  orphanage() {
    return request<{
      customers: CustomerDto[];
      documents: DocumentDto[];
      attachments: AttachmentDto[];
    }>(`/api/orphanage`);
  },

  // --- Attachments ---------------------------------------------------------
  listAttachments(opts: string | { customerId?: string; limit?: number; cursor?: string } = {}) {
    // Legacy signature accepted a bare customerId string. Normalise.
    const o = typeof opts === 'string' ? { customerId: opts } : opts;
    const params = new URLSearchParams();
    if (o.customerId) params.set('customerId', o.customerId);
    if (o.limit) params.set('limit', String(o.limit));
    if (o.cursor) params.set('cursor', o.cursor);
    const qs = params.toString() ? `?${params.toString()}` : '';
    return request<{ attachments: AttachmentDto[]; nextCursor?: string | null }>(
      `/api/attachments${qs}`,
    );
  },
  async uploadAttachment(body: {
    customerId: string;
    documentId?: string | null;
    metadataIv: string;
    metadataCiphertext: string;
    blobIv: string;
    blob: Blob;
  }): Promise<{ id: string; size: number }> {
    const form = new FormData();
    form.append('customerId', body.customerId);
    if (body.documentId) form.append('documentId', body.documentId);
    form.append('metadataIv', body.metadataIv);
    form.append('metadataCiphertext', body.metadataCiphertext);
    form.append('blobIv', body.blobIv);
    // Zero-knowledge invariant: the plaintext filename is already in the
    // encrypted `metadataCiphertext`. The third arg here sets the multipart
    // Content-Disposition `filename=…`, which WOULD leak to server/proxy
    // logs — use a generic placeholder instead.
    form.append('file', body.blob, 'blob');
    const tok = getAccessToken();
    const res = await fetch(`${BASE}/api/attachments`, {
      method: 'POST',
      headers: tok ? { Authorization: `Bearer ${tok}` } : undefined,
      body: form,
    });
    if (!res.ok) {
      let errBody: unknown;
      try {
        errBody = await res.json();
      } catch {
        errBody = { error: res.statusText };
      }
      const err = new Error(`${res.status}: ${JSON.stringify(errBody)}`) as Error & {
        status: number;
        body: unknown;
      };
      err.status = res.status;
      err.body = errBody;
      throw err;
    }
    return res.json();
  },
  deleteAttachment(id: string) {
    return request<{ ok: true }>(`/api/attachments/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });
  },
  restoreAttachment(id: string, metadata?: EncryptedPayload) {
    return request<{ ok: true }>(
      `/api/attachments/${encodeURIComponent(id)}/restore`,
      {
        method: 'POST',
        body: JSON.stringify(metadata ? { metadata } : {}),
      },
    );
  },
  /**
   * Ask the server to suggest an icon for a customer or page name. The name
   * is sent plaintext — a deliberate, scoped carve-out of the zero-knowledge
   * rule. The server calls Claude Haiku transiently and never persists the
   * name. Failures (offline, 503, rate-limited) should be silently ignored
   * by callers so a missing suggestion just leaves the default icon.
   */
  suggestIcon(body: SuggestIconRequest) {
    return request<SuggestIconResponse>(`/api/vault/suggest-icon`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  /**
   * Record a `vault_exported` audit entry. Called from ExportVaultModal
   * immediately before the ZIP is finalised. The request body carries the
   * counts that the client is about to write — an MSP admin can then see in
   * the audit log that user X exported N customers on date Y.
   */
  recordExportAudit(body: {
    itemCounts: { customers: number; documents: number; attachments: number };
    includeAttachments: boolean;
    totalBytes?: number;
  }) {
    // Server echoes back a provenance block the client bakes into the
    // archive's manifest — see ExportVaultModal.tsx.
    return request<{
      ok: true;
      auditLogId: string;
      vaultId: string;
      exportedBy: { userId: string; username: string };
      serverVersion: string;
      serverTimestamp: string;
    }>(`/api/vault/export-audit`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },
  async getAttachment(
    id: string,
  ): Promise<{ blob: Uint8Array; iv: string; metadata: EncryptedPayload }> {
    const tok = getAccessToken();
    const res = await fetch(`${BASE}/api/attachments/${encodeURIComponent(id)}`, {
      headers: tok ? { Authorization: `Bearer ${tok}` } : undefined,
    });
    if (!res.ok) {
      const err = new Error(`${res.status}: ${res.statusText}`) as Error & {
        status: number;
      };
      err.status = res.status;
      throw err;
    }
    const iv = res.headers.get('x-iv') ?? '';
    const metadataIv = res.headers.get('x-metadata-iv') ?? '';
    const metadataCiphertext = res.headers.get('x-metadata') ?? '';
    const buf = new Uint8Array(await res.arrayBuffer());
    return {
      blob: buf,
      iv,
      metadata: { iv: metadataIv, ciphertext: metadataCiphertext, version: 0 },
    };
  },
};

export interface CustomerDto {
  id: string;
  payload: EncryptedPayload;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AttachmentDto {
  id: string;
  customerId: string;
  documentId: string | null;
  metadata: EncryptedPayload;
  size: number;
  deletedAt: string | null;
  createdAt: string;
}

export interface DocumentDto {
  id: string;
  customerId: string;
  parentId: string | null;
  payload: EncryptedPayload;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lock?: {
    documentId: string;
    userId: string;
    username: string;
    lockedAt: string;
    expiresAt: string;
  } | null;
}
